import axios from "axios";
import { authService } from "../services/auth/authService";
import { getEnvironmentConfig } from "../utils/detectEnv";
import { reportNetworkError, reportNetworkSuccess } from "../utils/networkStatus";
import { isMaintenanceStatus, reportMaintenanceError, reportMaintenanceSuccess } from "../utils/maintenanceStatus";

// Get baseURL from environment config
const envConfig = getEnvironmentConfig();
const baseURL = envConfig.apiBaseUrl;

console.log('Axios baseURL configured:', baseURL);

const api = axios.create({
  baseURL: baseURL,
});

api.interceptors.request.use(
  (config) => {
    // Get access token from localStorage
    const accessToken = authService.getAccessToken();

    // Set X-nuid header
    config.headers["X-nuid"] = sessionStorage.getItem('userId')?.toUpperCase() ?? '';

    // Set Authorization header with Bearer token
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      console.log('Authorization header set with token');
    } else {
      config.headers["Authorization"] = '';
      console.log('Authorization header set to empty string (no token)');
    }

    try {
      const base = api.defaults.baseURL || "";
      const relative = String(config.url || "");
      const urlObj = new URL(relative, base);

      // Log original URL
      console.log('Request URL:', urlObj.toString());

      // Always append envlbl=drn or envlbl=dr for specific environments
      if (envConfig.env == 'drn') {
        urlObj.searchParams.append('envlbl', 'drn');
      } else if (envConfig.env == 'dr') {
        urlObj.searchParams.append('envlbl', 'dr');
      }
      console.log('Modified Request URL (envlbl added):', urlObj.toString());

      // Build merged params for axios config.params
      let paramsObj: Record<string, any> = {};

      if (config.params instanceof URLSearchParams) {
        paramsObj = Object.fromEntries(config.params.entries());
      } else if (config.params && typeof config.params === "object") {
        paramsObj = { ...(config.params as Record<string, any>) };
      }

      // Merge params from the modified URL (these include envlbl plus any that were in the URL)
      const urlParams = Object.fromEntries(urlObj.searchParams.entries());
      paramsObj = { ...paramsObj, ...urlParams };

      config.params = paramsObj;

      // Strip any query string from config.url to avoid double-encoding of params by axios
      if (typeof config.url === "string") {
        const [pathOnly] = relative.split("?");
        config.url = pathOnly;
      }
    } catch (e) {
      console.warn("Unable to build request URL for logging", e);
    }

    return config;
  },
  (error) => {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(error?.message ?? 'Request configuration failed');
  }
);

// Response interceptor — handles ERR_NETWORK (offline banner) and 401 (token refresh).
// See NETWORK_ERROR_HANDLING.md for the full detection flow.
api.interceptors.response.use(
  (response) => {
    // Signal recovery: the first successful response after an ERR_NETWORK
    // event notifies the OfflineBanner to restore the UI.
    reportNetworkSuccess();
    // Signal recovery: the first successful response after a 502/503 event
    // notifies the MaintenanceBanner to restore the UI.
    reportMaintenanceSuccess();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // ERR_NETWORK: request was sent but no HTTP response was received.
    // This is the most reliable cross-browser offline signal — Safari does
    // not consistently fire the browser `offline` event, but it does let
    // Axios throw ERR_NETWORK when a real API call fails due to no connectivity.
    // We only trigger the offline banner when the device itself has no network
    // connection (navigator.onLine === false). ERR_NETWORK can also fire when
    // a server is unreachable (e.g. 502 gateway, local proxy down) while the
    // device is still connected — that is a server error, not an offline state.
    if (!error.response && error.code === 'ERR_NETWORK') {
      console.error('Network Error:', {
        code: error.code,
        message: error.message,
        url: originalRequest?.url,
      });
      const deviceOffline = typeof navigator === 'undefined' || !navigator.onLine;
      if (deviceOffline) {
        reportNetworkError();
      }
      throw error;
    }

    // Planned maintenance: infrastructure returns 502 (Bad Gateway) or 503
    // (Service Unavailable) while a maintenance window is in effect. Show a
    // dedicated banner instead of a generic error so users understand the
    // outage is expected and temporary.
    if (isMaintenanceStatus(error.response?.status)) {
      reportMaintenanceError();
    }

    // If 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        console.log('Token expired - attempting to refresh...');
        const refreshed = await authService.refreshAccessToken();

        if (refreshed) {
          // Get new token and retry original request
          const newToken = authService.getAccessToken();
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Redirect to login
        authService.initiateLogin();
        if (refreshError instanceof Error) {
          throw refreshError;
        }
        throw new Error('Token refresh failed');
      }
    }

    // Ensure we're throwing an Error instance while preserving response data
    if (!(error instanceof Error)) {
      const errorMessage = error?.message ?? 'Request failed';
      const errorInstance = new Error(errorMessage) as any;
      errorInstance.response = error?.response;
      errorInstance.config = error?.config;
      errorInstance.request = error?.request;
      throw errorInstance;
    }
    throw error;
  }
);

export default api;